import { useState, useEffect, useCallback } from "react";
import { dashboardApi } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, Users, ShoppingCart, Award } from "lucide-react";
import DateRangePicker from "../components/DateRangePicker";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";

const CATEGORY_COLORS = {
  Books: "#D97706",
  Sculptures: "#78716C",
  Paintings: "#BE123C",
  Clothing: "#7C3AED",
  Jewelry: "#EAB308",
  "Home and Living": "#0D9488",
};

// Iter 134 — hoisted recharts prop objects so they stay referentially
// stable across renders (avoids unnecessary chart redraws).
const AXIS_TICK_MD = { fontSize: 12 };
const Y_TICK_K = (v) => `₹${v / 1000}k`;

const PerformancePage = () => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange?.from) params.date_from = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.date_to = format(dateRange.to, "yyyy-MM-dd");
      const response = await dashboardApi.getPerformance(params);
      setPerformance(response.data.performance);
    } catch (error) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const totalRevenue = performance.reduce((sum, p) => sum + p.total_revenue, 0);
  const totalOrders = performance.reduce((sum, p) => sum + p.total_orders, 0);
  const totalLeads = performance.reduce((sum, p) => sum + p.total_leads, 0);
  const avgConversion =
    performance.length > 0
      ? performance.reduce((sum, p) => sum + p.conversion_rate, 0) /
        performance.length
      : 0;

  // Prepare chart data
  const chartData = performance.map((p) => ({
    name: p.name.split(" ")[0],
    revenue: p.total_revenue,
    leads: p.total_leads,
    converted: p.converted_leads,
  }));

  // Category breakdown across all salespeople
  const categoryTotals = {};
  performance.forEach((p) => {
    Object.entries(p.category_breakdown || {}).forEach(([cat, amount]) => {
      categoryTotals[cat] = (categoryTotals[cat] || 0) + amount;
    });
  });

  const categoryData = Object.entries(categoryTotals)
    .map(([name, value]) => ({
      name,
      value,
      color: CATEGORY_COLORS[name] || "#A8A29E",
    }))
    .sort((a, b) => b.value - a.value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-fade-up">
        <div>
          <h1
            className="text-2xl lg:text-3xl font-semibold text-[--text-primary]"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Sales Performance
          </h1>
          <p className="text-sm text-[--text-muted] mt-1">
            Track team performance and revenue metrics
          </p>
        </div>
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stats-card" data-testid="stat-team-revenue">
          <div className="flex items-start justify-between">
            <div>
              <p className="stats-label">Team Revenue</p>
              <p className="stats-value mt-1">
                ₹{totalRevenue.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="p-2 bg-[#BC4B35]/10 rounded-md">
              <TrendingUp size={20} className="text-[#BC4B35]" />
            </div>
          </div>
        </div>

        <div className="stats-card" data-testid="stat-total-orders">
          <div className="flex items-start justify-between">
            <div>
              <p className="stats-label">Total Orders</p>
              <p className="stats-value mt-1">{totalOrders}</p>
            </div>
            <div className="p-2 bg-[#15803D]/10 rounded-md">
              <ShoppingCart size={20} className="text-[#15803D]" />
            </div>
          </div>
        </div>

        <div className="stats-card" data-testid="stat-total-leads">
          <div className="flex items-start justify-between">
            <div>
              <p className="stats-label">Total Leads</p>
              <p className="stats-value mt-1">{totalLeads}</p>
            </div>
            <div className="p-2 bg-[#312E81]/10 rounded-md">
              <Users size={20} className="text-[#312E81]" />
            </div>
          </div>
        </div>

        <div className="stats-card" data-testid="stat-avg-conversion">
          <div className="flex items-start justify-between">
            <div>
              <p className="stats-label">Avg Conversion</p>
              <p className="stats-value mt-1">{avgConversion.toFixed(1)}%</p>
            </div>
            <div className="p-2 bg-[#D97706]/10 rounded-md">
              <Award size={20} className="text-[#D97706]" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue by Salesperson */}
        <div className="bento-card">
          <h2
            className="text-xl font-medium text-[#1C1917] mb-4"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Revenue by Salesperson
          </h2>
          <div className="h-72" data-testid="revenue-chart">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" tick={AXIS_TICK_MD} />
                  <YAxis tickFormatter={Y_TICK_K} />
                  <Tooltip
                    formatter={(value) => [
                      `₹${value.toLocaleString("en-IN")}`,
                      "Revenue",
                    ]}
                  />
                  <Bar dataKey="revenue" fill="#BC4B35" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[#A8A29E]">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Leads vs Converted */}
        <div className="bento-card">
          <h2
            className="text-xl font-medium text-[#1C1917] mb-4"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Leads vs Conversions
          </h2>
          <div className="h-72" data-testid="leads-chart">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" tick={AXIS_TICK_MD} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="leads"
                    fill="#312E81"
                    radius={[4, 4, 0, 0]}
                    name="Total Leads"
                  />
                  <Bar
                    dataKey="converted"
                    fill="#15803D"
                    radius={[4, 4, 0, 0]}
                    name="Converted"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[#A8A29E]">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {categoryData.length > 0 && (
        <div className="bento-card mb-8">
          <h2
            className="text-xl font-medium text-[#1C1917] mb-4"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Revenue by Category
          </h2>
          <div className="h-64" data-testid="category-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis type="number" tickFormatter={Y_TICK_K} />
                <YAxis type="category" dataKey="name" width={120} tick={AXIS_TICK_MD} />
                <Tooltip
                  formatter={(value) => [
                    `₹${value.toLocaleString("en-IN")}`,
                    "Revenue",
                  ]}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {categoryData.map((entry, index) => (
                    <Bar key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Salesperson Leaderboard */}
      <div className="bento-card">
        <h2
          className="text-xl font-medium text-[#1C1917] mb-4"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          Salesperson Leaderboard
        </h2>

        {performance.length === 0 ? (
          <div className="empty-state py-8">
            <Users size={40} className="mx-auto mb-3 text-[#A8A29E]" />
            <p>No salespeople found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Salesperson</th>
                  <th>Revenue</th>
                  <th>Orders</th>
                  <th>Leads</th>
                  <th>Conversion</th>
                  <th>Top Category</th>
                </tr>
              </thead>
              <tbody>
                {performance.map((person, index) => {
                  const topCategory = Object.entries(
                    person.category_breakdown || {}
                  ).sort((a, b) => b[1] - a[1])[0];

                  return (
                    <tr key={person.id} data-testid={`performer-${person.id}`}>
                      <td>
                        <span
                          className={`
                          w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm
                          ${
                            index === 0
                              ? "bg-[#EAB308] text-white"
                              : index === 1
                              ? "bg-[#A8A29E] text-white"
                              : index === 2
                              ? "bg-[#CD7F32] text-white"
                              : "bg-[#F5F2EA] text-[#57534E]"
                          }
                        `}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="avatar">
                            {person.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-[#1C1917]">
                              {person.name}
                            </p>
                            <p className="text-sm text-[#57534E]">
                              {person.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="font-semibold text-[#15803D]">
                          ₹{person.total_revenue.toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td>{person.total_orders}</td>
                      <td>{person.total_leads}</td>
                      <td>
                        <span
                          className={`font-medium ${
                            person.conversion_rate >= 20
                              ? "text-[#15803D]"
                              : person.conversion_rate >= 10
                              ? "text-[#D97706]"
                              : "text-[#57534E]"
                          }`}
                        >
                          {person.conversion_rate}%
                        </span>
                      </td>
                      <td>
                        {topCategory ? (
                          <span
                            className={`category-badge category-${topCategory[0]
                              .toLowerCase()
                              .replace(/ /g, "-")}`}
                          >
                            {topCategory[0]}
                          </span>
                        ) : (
                          <span className="text-[#A8A29E]">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformancePage;
